import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { UnifiedChatWidget, UnifiedChatWidgetHandle } from "@/components/chat/UnifiedChatWidget";
import { ChatTheme } from "@/types/theme";
import { Loader2 } from "lucide-react";
import { useChatbot } from "@/hooks/useChatbot";

// This script will be injected into the page to prevent unloading
const KEEP_ALIVE_SCRIPT = `
  // Keep the page alive in the browser's memory when switching tabs
  window.addEventListener('beforeunload', function(e) {
    // Only prevent unloading when it's a tab switch, not a page navigation or reload
    if (document.visibilityState === 'hidden') {
      // The visibility state is 'hidden' when switching tabs
      e.preventDefault();
      e.returnValue = '';
      return '';
    }
  });
`;

const FullscreenChat = () => {
  const { chatId } = useParams();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [title, setTitle] = useState("OfferChat Support");
  const [transformedTheme, setTransformedTheme] = useState<ChatTheme | undefined>(undefined);

  // Track initialization state with refs to prevent infinite updates
  const isInitialized = useRef(false);
  const didProcessUrlTheme = useRef(false);
  const didProcessDBTheme = useRef(false);
  const chatWidgetRef = useRef<UnifiedChatWidgetHandle>(null);

  // Use the specific chatId if no chatId is in params (for direct access testing)
  const chatbotId = chatId || "a6765b82-3ff2-4755-888d-a9dd7d8375e7";

  // Inject a script to prevent the page from unloading when switching tabs
  useEffect(() => {
    // Add script to prevent tab unloading
    const script = document.createElement('script');
    script.textContent = KEEP_ALIVE_SCRIPT;
    document.head.appendChild(script);

    // Handle visibility change events
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Tab is being hidden (user switching tabs)
        console.log('Tab hidden - preserving chat state');

        // Chat widget state will be preserved automatically

        // Set a flag to detect tab switches
        sessionStorage.setItem('chat_tab_switched', 'true');
        sessionStorage.setItem('chat_last_hidden', new Date().toISOString());
      } else if (document.visibilityState === 'visible') {
        console.log('Tab visible again');

        // Check if this is a return from a tab switch
        if (sessionStorage.getItem('chat_tab_switched') === 'true') {
          console.log('Returned from tab switch - restoring state');

          // Chat widget state will be restored automatically

          // Clear the flag
          sessionStorage.removeItem('chat_tab_switched');
        }

        sessionStorage.setItem('chat_last_visible', new Date().toISOString());
      }
    };

    // Register event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Set meta tags to keep the page active
    const metaNoCache = document.createElement('meta');
    metaNoCache.setAttribute('http-equiv', 'Cache-Control');
    metaNoCache.setAttribute('content', 'no-store, no-cache, must-revalidate');
    document.head.appendChild(metaNoCache);

    const metaExpires = document.createElement('meta');
    metaExpires.setAttribute('http-equiv', 'Expires');
    metaExpires.setAttribute('content', '0');
    document.head.appendChild(metaExpires);

    return () => {
      // Clean up
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (script.parentNode) document.head.removeChild(script);
      if (metaNoCache.parentNode) document.head.removeChild(metaNoCache);
      if (metaExpires.parentNode) document.head.removeChild(metaExpires);
    };
  }, []);

  // Get theme from URL parameter (for live preview in builder)
  const processUrlTheme = () => {
    const themeParam = searchParams.get('theme');
    if (themeParam) {
      try {
        const parsedTheme = JSON.parse(decodeURIComponent(themeParam)) as Partial<ChatTheme>;

        // Validate the theme object has the expected properties
        if (parsedTheme && typeof parsedTheme === 'object') {
          // Create validated theme with defaults
          const validatedTheme = {...parsedTheme};

          // Ensure font family has a default
          if (!validatedTheme.fontfamily || validatedTheme.fontfamily.trim() === '') {
            validatedTheme.fontfamily = 'Arial, sans-serif';
            console.log('Using default font family:', validatedTheme.fontfamily);
          } else {
            console.log('Using provided font family:', validatedTheme.fontfamily);
          }

          // Validate all colors
          const colorKeys = Object.keys(validatedTheme).filter(key =>
            typeof validatedTheme[key] === 'string' && key.toLowerCase().includes('color')
          );

          // Ensure all color values are valid
          colorKeys.forEach(key => {
            const value = validatedTheme[key] as string;
            if (!value || value.trim() === '' || !value.startsWith('#')) {
              // Provide default colors based on the key
              const defaultColor = key.toLowerCase().includes('text') ? '#000000' : '#4FD1C5';
              console.log(`Fixing invalid color value for ${key}:`, value, 'to', defaultColor);
              validatedTheme[key] = defaultColor;
            }
          });

          console.log('Validated theme:', validatedTheme);
          setTransformedTheme(validatedTheme as ChatTheme);
          setIsLoading(false);
          didProcessUrlTheme.current = true;

          // Check for new deployment via cache bust and clear localStorage if detected
          const cacheBust = searchParams.get('cacheBust');
          if (cacheBust) {
            const lastCacheBust = localStorage.getItem('chatCacheBust');

            if (!lastCacheBust || lastCacheBust !== cacheBust) {
              console.log('New deployment detected via cache bust - clearing state');
              localStorage.setItem('chatCacheBust', cacheBust);

              // Clear all chat-related localStorage items
              Object.keys(localStorage).forEach(key => {
                if (key.startsWith('chat_') || key.includes('flow')) {
                  localStorage.removeItem(key);
                }
              });
            }
          }

          return true;
        }
      } catch (err) {
        console.error("Error parsing theme from URL:", err);
        // Force loading to complete even on error
        setIsLoading(false);
      }
    }
    return false;
  };

  // Use the useChatbot hook with the chatbotId
  const {
    chatbot,
    theme: themeData,
    questions: transformedQuestions,
    triggerRules: transformedTriggerRules,
    isLoading: isLoadingQueries,
  } = useChatbot(chatbotId);

  // Cache invalidation logic - clear old cached state
  useEffect(() => {
    // Check if this is a new load after deployment via cacheBust param
    const lastDeploymentTime = localStorage.getItem('chat_last_deployment_time');
    const cacheBust = searchParams.get('cacheBust'); // Get cacheBust param

    // Only clear cache if cacheBust parameter exists and is different from the last known one
    if (cacheBust && cacheBust !== lastDeploymentTime) {
      console.log(`New deployment detected (cacheBust: ${cacheBust}) - clearing cached state`);
      // Clear all chat-related localStorage items
      Object.keys(localStorage)
        .filter(key => key.startsWith('chatflow_') || key.startsWith('chat_')) // Use startsWith
        .forEach(key => {
          console.log(`Removing localStorage key: ${key}`); // Add logging
          localStorage.removeItem(key);
        });

      // Set new deployment time using the cacheBust value
      localStorage.setItem('chat_last_deployment_time', cacheBust);

    } else if (!lastDeploymentTime && cacheBust) {
      // If no previous time, but cacheBust exists, store it for next time
      localStorage.setItem('chat_last_deployment_time', cacheBust);
    }
    // If cacheBust is missing or matches lastDeploymentTime, DO NOTHING - preserve cache.

  }, [searchParams]); // Depend only on searchParams

  // Check for URL theme first - this only runs once
  useEffect(() => {
    if (!isInitialized.current) {
      const hasUrlTheme = processUrlTheme();
      if (hasUrlTheme) {
        console.log("Using theme from URL parameter");
      }
    }
  }, [searchParams]); // Only depend on searchParams

  // Process database theme if no URL theme is found
  useEffect(() => {
    // Skip if already initialized with URL theme or if already processed DB theme
    if (didProcessUrlTheme.current || didProcessDBTheme.current) {
      return;
    }

    // Wait until queries are loaded
    if (!isLoadingQueries && chatbot && themeData) {
      console.log("Using theme from database");

      // Set theme from database - ensuring all ChatTheme properties are present
      // Provide defaults for potentially missing fields from the database
      setTransformedTheme({
        id: themeData.id,
        chatbot_id: themeData.chatbot_id,
        name: themeData.name || 'Database Theme',
        primarycolor: themeData.primarycolor || '#2563EB',
        backgroundcolor: themeData.backgroundcolor || '#FFFFFF',
        userbubblecolor: themeData.userbubblecolor || '#2563EB',
        botbubblecolor: themeData.botbubblecolor || '#F5F5F5',
        usertextcolor: themeData.usertextcolor || '#FFFFFF',
        bottextcolor: themeData.bottextcolor || '#000000',
        headercolor: themeData.headercolor || themeData.primarycolor || '#2563EB', // Fallback to primary
        headertextcolor: themeData.headertextcolor || '#FFFFFF',
        borderradius: themeData.borderradius ?? '1rem', // Use nullish coalescing for 0
        fontfamily: themeData.fontfamily || 'Arial, sans-serif',
        buttoncolor: themeData.buttoncolor || themeData.primarycolor || '#2563EB', // Fallback to primary
        buttontextcolor: themeData.buttontextcolor || '#FFFFFF',
        position: themeData.position || 'right',
        buttontext: themeData.buttontext || 'Chat With Us',
        welcomebubblecolor: themeData.welcomebubblecolor || themeData.botbubblecolor || '#F5F5F5', // Fallback to bot bubble
        welcomebubbletextcolor: themeData.welcomebubbletextcolor || themeData.bottextcolor || '#000000', // Fallback to bot text
        inputbordercolor: themeData.inputbordercolor || '#E2E8F0',
        inputborderradius: themeData.inputborderradius ?? '0.5rem',
        inputbackgroundcolor: themeData.inputbackgroundcolor || '#FFFFFF',
        avatarbackgroundcolor: themeData.avatarbackgroundcolor || themeData.primarycolor || '#2563EB', // Fallback to primary
        headerbackgroundcolor: themeData.headerbackgroundcolor || themeData.headercolor || '#2563EB', // Fallback to header
        showAvatar: themeData.showavatar ?? themeData.showAvatar ?? true,
        showTitle: themeData.showtitle ?? themeData.showTitle ?? true,
        showCloseButton: themeData.showclosebutton ?? themeData.showCloseButton ?? true,
        showReset: themeData.showreset ?? themeData.showReset ?? true,
        showTimestamp: themeData.showtimestamp ?? themeData.showTimestamp ?? true,
        showSources: themeData.showsources ?? themeData.showSources ?? false,
        showTypingIndicator: themeData.showtypingindicator ?? themeData.showTypingIndicator ?? true,
        showUserAvatar: themeData.showuseravatar ?? themeData.showUserAvatar ?? true,
        created_at: themeData.created_at,
        updated_at: themeData.updated_at
      });

      // Set title from database
      setTitle(chatbot.title || "OfferChat Support");
      setIsLoading(false);
      didProcessDBTheme.current = true;
      isInitialized.current = true;
    }
  }, [isLoadingQueries, chatbot, themeData]); // Don't include transformedQuestions

  // Wait for essential data before rendering to prevent "chat not found" errors
  const isEssentialDataLoaded = !isLoadingQueries && chatbot;

  // Set loading to false only when we have essential data or there's an error
  useEffect(() => {
    if (isEssentialDataLoaded || (!isLoadingQueries && !chatbot)) {
      console.log('Essential data loaded, rendering chat widget');
      setIsLoading(false);
    }
  }, [isEssentialDataLoaded, isLoadingQueries, chatbot]);

  // Ensure we always have a valid theme, even if loading fails
  const defaultTheme: ChatTheme = {
    id: 'default',
    chatbot_id: chatbotId || '',
    name: 'Default Theme',
    primarycolor: '#2563EB',
    backgroundcolor: '#FFFFFF',
    userbubblecolor: '#2563EB',
    botbubblecolor: '#F3F4F6',
    usertextcolor: '#FFFFFF',
    bottextcolor: '#1F2937',
    headercolor: '#2563EB',
    headertextcolor: '#FFFFFF',
    borderradius: '0.75rem',
    fontfamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    buttoncolor: '#2563EB',
    buttontextcolor: '#FFFFFF',
    position: 'right',
    buttontext: 'Chat With Us',
    welcomebubblecolor: '#EFF6FF',
    welcomebubbletextcolor: '#1E40AF',
    inputbordercolor: '#E5E7EB',
    inputborderradius: '0.5rem',
    inputbackgroundcolor: '#FFFFFF',
    avatarbackgroundcolor: '#4FD1C5',
    headerbackgroundcolor: '#4FD1C5',
    showAvatar: true,
    showTitle: true,
    showCloseButton: true,
    showReset: true,
    showTimestamp: true,
    showSources: false,
    showTypingIndicator: true,
    showUserAvatar: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const finalTheme = transformedTheme || defaultTheme;

  // Show loading state while essential data is loading
  if (isLoading || isLoadingQueries) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-gray-600">Loading chat...</p>
        </div>
      </div>
    );
  }

  // Show error state if chatbot not found
  if (!isLoadingQueries && !chatbot) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4 text-center">
          <div className="text-red-500 text-6xl">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800">Chat Not Found</h2>
          <p className="text-gray-600 max-w-md">
            The chat you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen flex items-center justify-center bg-gray-50"
      data-testid="fullscreen-chat"
      data-chatbot-id={chatbotId}
    >
      <div className="w-full h-full md:shadow-2xl md:rounded-2xl overflow-hidden">
        <UnifiedChatWidget
          ref={chatWidgetRef}
          theme={finalTheme}
          isFullscreen
          questions={transformedQuestions || []}
          triggerRules={transformedTriggerRules || []}
          welcomeMessage={chatbot?.welcome_message || 'Welcome! How can I help you today?'}
          completionMessage={chatbot?.completion_message || 'Thank you for chatting with us!'}
          chatbotId={chatbotId}
          title={title || 'Chat With Us'}
          avatarUrl={chatbot?.avatar_url}
          operatorName={chatbot?.operator_name || 'Support'}
          companyName={chatbot?.company_name || 'OfferChat.ai'}
          companyLogo={chatbot?.company_logo}
        />
      </div>
    </div>
  );
};

export default FullscreenChat;
